import { defineStore } from "pinia";
import {
  computed,
  ComputedRef,
  inject,
  reactive,
  Reactive,
  ref,
  Ref,
  toRaw
} from "vue";
import { useRouter } from "vue-router";
import axios, { AxiosResponse } from "axios";
import * as uuid from "uuid";

import blueprints from "../data/generated/blueprints.json";
import elementInputs from "@/data/generated/inputLocation.json";

import { useTreeStore } from "./treeStore";
import { useMainStore } from "./mainStore";
import { useAxiosStore } from "./axiosStore";
import { useCalculationStore } from "./calculationStore";
import { isLocationType } from "@/utils/helper";
import { Nullable } from "vitest";
import { fieldStatus } from "@/types/global";
import { useSocketStore } from "./socketStore";
import * as Sentry from "@sentry/vue";

export const useSpaceStore = defineStore("spaceStore", () => {
  const router = useRouter();
  const rustWorker: Worker = inject("rustWorker");

  const treeStore = useTreeStore();
  const mainStore = useMainStore();
  const axiosStore = useAxiosStore();
  const socketStore = useSocketStore();
  const calculationStore = useCalculationStore();

  const readOnly = computed(() => {
    return (
      mainStore.calculationSpaces.find(
        (space) => space.id === mainStore.activeSpaceId
      )?.rights === 4
    );
  });

  const calculationElements: Record<string, calculationElement> = reactive({});

  // local logStorage
  const localLogStorage = {
    startTime: Date.now(),
    logs: []
  };

  let intervalId = null;

  function createInterval() {
    intervalId = setInterval(() => {
      checkIfEverythingIsCorrectlyStored();
    }, 60000);
  }

  async function checkIfEverythingIsCorrectlyStored() {
    const mapFunction = (element) =>
      element.action +
      "_" +
      element.element +
      "|" +
      element.field +
      ": " +
      element.value;

    if (!mainStore.activeSpaceId) {
      // no space id
      Sentry.captureException("No active space ID");
      return Promise.reject();
    }

    return axios
      .get(
        `/api/element/${mainStore.activeSpaceId}/logs/${localLogStorage.startTime}`
      )
      .then((response) => {
        const server = JSON.stringify(response.data.map(mapFunction).sort());
        const client = JSON.stringify(
          localLogStorage.logs.map(mapFunction).sort()
        );

        if (server === client) {
          console.log("Everything is correctly stored");
        } else {
          console.log("Something is wrong");
          Sentry.captureException("Not everything is correctly stored", {
            extra: {
              server,
              client
            }
          });
        }
      });
  }

  // let logDb;
  // const request = window.indexedDB.open("log-db", 1);
  // request.onerror = (event) => {
  //   console.error("Why didn't you allow my web app to use IndexedDB?!");
  // };
  // request.onsuccess = (event) => {
  //   logDb = event.target.result;
  // };
  // request.onupgradeneeded = () => {
  //   let db = request.result;

  //   db.createObjectStore("log-store", {
  //     keyPath: "id",
  //     autoIncrement: true
  //   });
  // };

  const missingInputs: Ref<string[]> = ref([]);

  const fieldStatus: ComputedRef<fieldStatus> = computed(() => {
    const result = {
      field: {},
      tab: {},
      element: {}
    };
    for (const element of missingInputs.value) {
      const [id, field] = element.split("|");
      const [real_id, type] = id.split("_");

      for (const [subType, tabs] of Object.entries(elementInputs[type])) {
        for (const [tab, fields] of Object.entries(tabs)) {
          if (fields.includes(field)) {
            result.field[id + "|" + field] = "error";
            result.tab[id + "|" + tab] = "error";

            // result.tab[real_id + "_" + subType + "|" + tab] = "error";
            result.element[real_id + "_" + subType] = "error";
          }
        }
      }
    }
    return result;
  });

  function clearData() {
    rustWorker.postMessage({
      task: "clearData"
    });
    // spaceStore delete all elements
    Object.keys(calculationElements).forEach(
      (key) => delete calculationElements[key]
    );
    treeStore.elementTree.length = 0;
    localLogStorage.logs.length = 0;
    localLogStorage.startTime = Date.now();
    clearInterval(intervalId);
  }

  async function loadAllElements() {
    createInterval();
    loadMissingInputs();
    console.log("Loading elements...");
    return axios
      .get(`/api/element/${mainStore.activeSpaceId}/all`)
      .catch((error) => {
        console.error(error);
      })
      .then((response: AxiosResponse<any, any>) =>
        handleLoadedData(response.data)
      );
  }

  function handleLoadedData(data: BackendData) {
    const rootElements: string[] = [];

    // iterate over every element type and save to calculationElements
    for (const [type, elements] of Object.entries(data)) {
      for (const element of elements) {
        calculationElements[element.id + "_" + type] = element;

        // if an element has no parent and isnt a location, it must be a folder
        if (!element.parent && type !== "location") {
          rootElements.push(element.id + "_" + type);
        }
      }
    }

    // copy data to rust
    rustWorker.postMessage({
      task: "init",
      data: { data: toRaw(calculationElements) }
    });

    // create tree
    treeStore.elementTree.push(...treeStore.createHierachy(rootElements));
  }

  async function saveElement(id: string, ignoreParent: boolean = false) {
    const [realId, type] = id.split("_");

    console.log("[INFO]", "[SaveElement]", "init save element", realId, type);

    axiosStore.addElementRequest(
      {
        method: "POST",
        url: `/api/element/${mainStore.activeSpaceId}/${type}`,
        data: calculationElements[id]
      },
      id
    );

    updateParent(id);

    // calculate all elements
    // calculationStore.addElementCalculation(id);
    // calculationStore.startCalculating();
  }

  async function updateParent(
    id: string,
    remove: boolean = false,
    fromSocket: boolean = false
  ) {
    if (calculationElements[id].parent) {
      const [realId, type] = id.split("_");
      if (remove) {
        calculationElements[calculationElements[id].parent].children =
          calculationElements[calculationElements[id].parent].children.filter(
            (e) => e !== id
          );
      } else {
        calculationElements[calculationElements[id].parent].children.push(id);
      }

      rustWorker.postMessage({
        task: "change",
        data: {
          id: calculationElements[id].parent,
          field: "children",
          value: toRaw(
            calculationElements[calculationElements[id].parent].children
          )
        }
      });

      !fromSocket &&
        updateElement(calculationElements[id].parent, ["children"]);
    }
  }

  async function updateElement(
    id: string,
    fields: string[],
    inputValue: boolean = false
  ) {
    if (fields.length === 0) {
      return Promise.resolve();
    }
    const [real_id, type] = id.split("_");
    const data = fields.reduce((result, field) => {
      result[field] = calculationElements[id][field];
      return result;
    }, {});

    axiosStore.addElementRequest(
      {
        method: "PUT",
        url:
          `/api/element/${mainStore.activeSpaceId}/${type}/${real_id}` +
          (inputValue ? `?inputValue=${inputValue}` : ""),
        data
      },
      id + (inputValue ? "input" : "")
    );

    if (inputValue) {
      const [field, value] = Object.entries(data)[0];
      localLogStorage.logs.push({
        element: id,
        action: "update",
        field,
        value
      });
      // const transaction = logDb.transaction("log-store", "readwrite");
      // const [field, value] = Object.entries(data)[0];
      // transaction
      //   .objectStore("log-store")
      //   .put({ element: id, action: "update", field, value });
    }
  }

  /** Delete an existing element
   *
   * @param {string} id format: uuid_type (e.g.: 1234-1234-1234-1234_location)
   * @returns {Promise}
   */
  async function removeElement(
    id: string,
    parent: string = undefined,
    variant: boolean = false,
    fromSocket: boolean = false
  ): Promise<any> {
    const [realId, type] = id.split("_");
    const treeId = variant ? `${realId}_variant` : id;

    // if (copyData.id === id) {
    //   copyData.id = null;
    // }
    if (variant && !parent) {
      parent = treeStore.elementBreadcrumbs[treeId].at(-2).id;
    }

    parent = parent || calculationElements[id].parent;

    console.log("delete element", id, treeId, parent, variant);

    await treeStore.deleteInHierachy(treeId, parent, variant);

    console.log("After routing");
    updateParent(id, true, fromSocket);

    function removeChildren(id: string, variant: string = undefined) {
      const children = calculationElements[id].children;

      for (const child_id of children) {
        if (!child_id || !calculationElements.hasOwnProperty(child_id)) {
          continue;
        }

        if (variant && calculationElements[child_id]?.variant !== variant) {
          continue;
        }

        removeChildren(child_id);

        rustWorker.postMessage({
          task: "deleteElement",
          data: {
            id: child_id
          }
        });

        delete calculationElements[child_id];

        const [realId, type] = child_id.split("_");
        !fromSocket &&
          axiosStore.deleteRequest(
            {
              method: "DELETE",
              url: `/api/element/${mainStore.activeSpaceId}/${type}/${realId}`
            },
            child_id
          );
      }
    }

    removeChildren(id);

    console.log("Delete other variants", id.endsWith("part"), !variant);
    if (id.endsWith("part") && !variant) {
      removeChildren(parent, calculationElements[id].variant);
    }

    calculationStore.removeElementCalculation(id, fromSocket);

    rustWorker.postMessage({
      task: "deleteElement",
      data: {
        id: id
      }
    });

    calculationStore.startCalculating();

    if (parent) {
      const [real_parent_id, parent_type] = parent.split("_");
      // router.push({ name: "calculations-" + parent_type, params: { nodeId: real_parent_id } });
    }

    delete calculationElements[id];

    !fromSocket &&
      axiosStore.deleteRequest(
        {
          method: "DELETE",
          url: `/api/element/${mainStore.activeSpaceId}/${type}/${realId}?variant=${variant}`
        },
        id
      );
  }

  /** Create a new element or clone an existing element
   *
   * @param {string} type e.g. location
   * @param {string | undefined} parent format: uuid_type (e.g.: 1234-1234-1234-1234_location)
   *
   * @return {string} id of created element
   */
  function createElement(
    type: string,
    parent: string | undefined = undefined
  ): string {
    if (type === "variant") {
      return createVariant(parent);
    }

    const element: calculationElement = structuredClone(blueprints[type]);

    element.id = uuid.v4();
    const completeId = element.id + "_" + type;

    element.parent = parent;

    if (type === "part") {
      element.variant = element.id;
    }

    if (parent) {
      const parentTyp = parent.split("_")[1];
      if (parentTyp === "part" || parentTyp === "location") {
        element.location = parent;
      } else {
        element.location = calculationElements[parent].location ?? "";
      }

      if (treeStore.elementLocation[element.parent]) {
        treeStore.elementLocation[completeId] =
          treeStore.elementLocation[element.parent];
      }
    }

    calculationElements[completeId] = element;

    rustWorker.postMessage({
      task: "addElement",
      data: { id: completeId, data: element }
    });

    saveElement(completeId);

    if (!isLocationType(completeId)) {
      treeStore.addToHierarchy(completeId, parent);
    }

    return completeId;
  }

  function createVariant(partId: string): string {
    const element: calculationElement = structuredClone(blueprints["part"]);

    const part = calculationElements[partId];

    element.id = uuid.v4();
    element.parent = part.parent;
    element.location = part.location;
    element.variant = part.variant;
    element.is_active_part = 0;

    element.name = calculationElements[partId].name;

    const completeId = element.id + "_part";

    calculationElements[completeId] = element;

    rustWorker.postMessage({
      task: "addElement",
      data: { id: completeId, data: element }
    });

    saveElement(completeId);

    if (treeStore.elementLocation[element.parent]) {
      treeStore.elementLocation[completeId] =
        treeStore.elementLocation[element.parent];
    }

    treeStore.addVariantToHierarchy(completeId, partId);

    return element.id + "_variant";
  }

  /** Move an element with all children to a new parent
   *
   * @param id element to move
   * @param newParent destination id
   * @returns
   */
  function moveElement(
    id: string,
    newParent: string,
    fromSocket: boolean = false
  ) {
    !fromSocket && socketStore.socket.emit("move-element", id, newParent);
    // before changing anything, calculate element as deleted
    calculationStore.removeElementCalculation(id, fromSocket);

    // TODO check if necessary
    newParent = newParent
      .replace("_manufacturingStep", "_part")
      .replace("_variant", "_part");

    // save old parent to recalculate it later
    const oldParent = calculationElements[id].parent;

    // change parent
    calculationElements[id].parent = newParent;
    calculationElements[newParent].children.push(id);
    calculationElements[oldParent].children.splice(
      calculationElements[oldParent].children.indexOf(id),
      1
    );

    // change in rust
    rustWorker.postMessage({
      task: "change",
      data: {
        id,
        field: "parent",
        value: newParent
      }
    });

    rustWorker.postMessage({
      task: "change",
      data: {
        id: oldParent,
        field: "children",
        value: toRaw(calculationElements[oldParent].children)
      }
    });

    rustWorker.postMessage({
      task: "change",
      data: {
        id: newParent,
        field: "children",
        value: toRaw(calculationElements[newParent].children)
      }
    });

    // change location if neccesarry
    if (
      calculationElements[id].location.endsWith("_location") ||
      calculationElements[id].location === ""
    ) {
      // Dont do anything, because  location is explicit set in moved element
      // return;
    } else if (calculationElements[id].parent.endsWith("_part")) {
      calculationElements[id].location = calculationElements[id].parent;
    } else {
      calculationElements[id].location =
        calculationElements[calculationElements[id].parent].location;
    }

    const changedElementLocations = treeStore.changeRealLocations(id);

    !fromSocket && updateMissingInputs(changedElementLocations[1]);

    rustWorker.postMessage({
      task: "change",
      data: {
        id,
        field: "location",
        value: calculationElements[id].location
      }
    });

    !fromSocket && updateElement(id, ["parent", "location"]);
    !fromSocket && updateElement(oldParent, ["children"]);
    !fromSocket && updateElement(newParent, ["children"]);

    calculationStore.addElementCalculation(id, [], fromSocket);
    calculationStore.addElementCalculation(
      id,
      changedElementLocations[0],
      fromSocket
    );
    calculationStore.startCalculating();

    // rebuild elementTree List
    // TODO only rebuild changed parts
    treeStore.elementTree.splice(
      0,
      Infinity,
      ...treeStore.createHierachy(
        treeStore.elementTree.map((treeElement) => treeElement.id)
      )
    );
  }

  function setDefault() {
    axiosStore.execute({
      method: "POST",
      url: "/api/calculation-spaces/" + mainStore.activeSpaceId + "/set-default"
    });
  }

  function getElement(id: string): Nullable<calculationElement> {
    if (!calculationElements.hasOwnProperty(id)) {
      return null;
    }

    return calculationElements[id];
  }

  function includesLevel(id: string, levels: number): boolean {
    const element = getElement(id);
    if (!element) {
      console.log("element not found", id, levels);
      return false;
    }

    // level 1 = 1 (bin 001)
    // level 2 = 2 (bin 010)
    // level 3 = 4 (bin 100)
    // console.log(id, element.level, levels);
    return !!(Number(element.level) & levels) || element.level === undefined;
  }

  function getElementList(
    parentId: string,
    type: elementType
  ): calculationElement[] {
    return Object.entries(calculationElements)
      .filter(([key, entry]) => {
        // key.endsWith(type) && console.log("type found", key);
        // entry.parent === parentId && console.log("Parent found", key);
        return key.endsWith(type) && entry.parent === parentId;
      })
      .reduce((resultArray, [key, value]) => {
        resultArray.push({ ...value, fullId: key });
        return resultArray;
      }, []);
  }

  function prepareClone(elementId: string) {
    // fetch all elements that are connected to this
    console.log("prepareClone", elementId);
    const ids = new Set<string>();
    const locations = new Set<string>();

    function recursiveSearch(id: string) {
      const element = getElement(id);
      if (ids.has(id) || !element) {
        return;
      }
      ids.add(id);

      if (
        element.location?.endsWith("location") &&
        !locations.has(element.location)
      ) {
        locations.add(element.location);
        recursiveSearch(element.location);
      }

      for (const child of element.children) {
        recursiveSearch(child);
      }
    }

    recursiveSearch(elementId);

    if (elementId.endsWith("part")) {
      let part = getElement(elementId);
      let extras = [];
      for (const child of getElement(part.parent).children) {
        if (
          child &&
          child.endsWith("part") &&
          getElement(child) &&
          getElement(child).variant === part.variant &&
          child !== elementId
        ) {
          extras.push(child);
          recursiveSearch(child);
        }
      }

      mainStore.copyData.extra = extras;
    }

    mainStore.copyData.allElements = Array.from(ids);
    mainStore.copyData.locations = Array.from(locations);
    mainStore.copyData.id = elementId;

    mainStore.copyData.name = getElement(elementId)?.name || "";
    mainStore.copyData.spaceId = getElement(elementId)?.space_id || "";
    mainStore.copyData.variantName = getElement(elementId)?.variant_name || "";
  }

  function cloneOnServer(
    parentId: string,
    cloneLocations: boolean,
    variant: boolean = false
  ) {
    axios
      .post(`/api/element/${mainStore.activeSpaceId}/clone`, {
        elementId: variant
          ? mainStore.copyData.id.replace("part", "variant")
          : mainStore.copyData.id,
        parentId,
        allElements: mainStore.copyData.allElements,
        cloneLocations: cloneLocations ? mainStore.copyData.locations : [],
        extra: {
          real_parent: calculationElements[parentId]?.parent,
          elements: variant ? mainStore.copyData.extra : []
        }
      })
      .then(() => {
        // Handle success or failure here
      })
      .catch((error) => {
        console.error("Error cloning element:", error);
        Sentry.captureException(error, {
          extra: {
            copyData: mainStore.copyData,
            parent: parentId,
            real_parent_id: calculationElements[parentId]?.parent,
            cloneLocations
          }
        });
      });
  }

  async function loadMissingInputs() {
    axios
      .get("/api/element/" + mainStore.activeSpaceId + "/missingInputs")
      .then((response) => {
        response.data.forEach((element: string) => {
          missingInputs.value.push(element);
        });
      });
  }

  function updateMissingInputs(missingInputs: string[]) {
    axios
      .post("/api/element/" + mainStore.activeSpaceId + "/missingInput", {
        values: missingInputs
      })
      .then((result) => {
        console.log("Missing inputs result:", result);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  function removeMissingInput(
    element_id: string,
    field: string,
    notFromSocket: boolean = true
  ) {
    if (fieldStatus.value.field[element_id + "|" + field]) {
      missingInputs.value = missingInputs.value.filter(
        (item) => item !== element_id + "|" + field
      );
      notFromSocket &&
        axios.delete(
          "/api/element/" +
            mainStore.activeSpaceId +
            "/missingInput/" +
            element_id +
            "|" +
            field
        );
    }
  }

  return {
    calculationElements,
    missingInputs,
    fieldStatus,
    clearData,
    loadAllElements,
    handleLoadedData,
    checkIfEverythingIsCorrectlyStored,
    saveElement,
    updateElement,
    updateParent,
    removeElement,
    createElement,
    createVariant,
    moveElement,
    setDefault,
    getElement,
    getElementList,
    includesLevel,
    prepareClone,
    cloneOnServer,
    loadMissingInputs,
    updateMissingInputs,
    removeMissingInput,
    readOnly
  };
});
